<template>
	<div>
		<vue-datepicker
			ref="vueDatePicker"
			type="date"
			:confirm-text="$tc('actions.select')"
			class="pixellot-date-range-picker"
			popup-class="pixellot-date-range-picker-popup"
			:value="value"
			:open.sync="isOpen"
			:disabled="disabled"
			:shortcuts="updatedShortcuts"
			:format="format"
			:confirm="true"
			:range="isRange"
			:append-to-body="false"
			:lang="language"
			@input="onInput"
			@pick="onPickDate"
			@close="onClose"
		>
			<template #icon-calendar>
				<SVGIcon :icon-name="hasValue ? 'schedule-check' : 'schedule'" />
			</template>
			<template #input>
				<div />
			</template>
			<template #footer>
				<div class="d-inline-block mr-10">
					<button class="mx-btn mx-datepicker-btn-confirm mr-25" @click="onClearDate">
						{{ $tc('actions.clear') }}
					</button>
				</div>
			</template>
		</vue-datepicker>
	</div>
</template>

<script>
import VueDatepicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/de'
import 'vue2-datepicker/locale/en'
import 'vue2-datepicker/locale/fr'
import 'vue2-datepicker/locale/es'
import 'vue2-datepicker/locale/hu'

import { SVGIcon } from '~/components/interface'

const getDateByInterval = (intervalEnd = 0, intervalStart = 0) => {
	const start = new Date()
	const end = new Date()
	const timeStart2Add = intervalStart * 24 * 3600 * 1000
	const timeEnd2Add = intervalEnd * 24 * 3600 * 1000

	start.setTime(start.getTime() + timeStart2Add)
	start.setHours(0, 0, 0, 0)

	end.setTime(end.getTime() + timeEnd2Add)
	end.setHours(23, 59, 59, 999)

	return intervalEnd >= intervalStart ? [start, end] : [end, start]
}

const shortcuts = that => [
	{
		text: that.$tc('components.dateRangePicker.todayText'),
		onClick: () => getDateByInterval()
	},
	{
		text: that.$tc('components.dateRangePicker.yesterdayText'),
		onClick: () => getDateByInterval(-1, -1)
	},
	{
		text: that.$tc('components.dateRangePicker.lastWeekText'),
		onClick: () => getDateByInterval(-7)
	},
	{
		text: that.$tc('components.dateRangePicker.lastTwoWeekText'),
		onClick: () => getDateByInterval(-14)
	},
	{
		text: that.$tc('components.dateRangePicker.lastMonthText'),
		onClick: () => getDateByInterval(-30)
	}
]

export default {
	name: 'DateRangeSelector',
	components: {
		VueDatepicker,
		SVGIcon
	},
	props: {
		value: {
			type: [Array, Object, Date],
			required: true
		},
		disabled: {
			type: Boolean,
			default: false
		},
		format: {
			type: String,
			default: 'DD/MM/YY'
		}
	},
	data: () => ({
		isOpen: false,
		isRange: true,
		pickedDate: null,
		language: 'en'
	}),
	computed: {
		hasValue () {
			return this.isRange
				? this.value.length ? this.value.every(Boolean) : false
				: Boolean(this.value)
		},
		updatedShortcuts () {
			return shortcuts(this)
		}
	},
	watch: {
		'$i18n.localeProperties.code': {
			immediate: true,
			handler (code) {
				this.language = code
			}
		}
	},
	methods: {
		isSingleDateSelected () {
			const selectedDates = this.$refs.vueDatePicker?.$refs?.picker?.innerValue
			const isValidSecondSelection = Array.isArray(selectedDates) &&
			 selectedDates[1] instanceof Date && !isNaN(selectedDates[1].getTime())

			if (!selectedDates || !isValidSecondSelection) {
				return true
			}

			return false
		},
		onClose () {
			this.pickedDate = null
		},
		onPickDate (value) {
			this.pickedDate = value
		},
		onInput (value) {
			if (Array.isArray(value)) {
				const isSingleSelection = this.pickedDate && this.isSingleDateSelected()

				if (value.length && !isSingleSelection) {
					const [start, end] = value

					start?.setHours(0, 0, 0, 0)
					end?.setHours(23, 59, 59, 999)

					this.$emit('input', value)
				} else if (isSingleSelection) {
					const start = new Date(this.pickedDate.valueOf())
					const end = new Date(this.pickedDate.valueOf())

					start?.setHours(0, 0, 0, 0)
					end?.setHours(23, 59, 59, 999)

					this.$emit('input', [start, end])
				}
			} else if (this.isRange) {
				this.$emit('input', [value, new Date()])
			} else {
				this.$emit('input', value)
			}
		},
		onClearDate () {
			if (this.isRange) {
				this.$emit('input', [])
			} else {
				this.$emit('input', null)
			}
		}
	}
}
</script>

<style lang="scss">
	$textColor: #3D3C90;
	$sideMenuBackground: #4c5e70;
	$calendarBackgroundImage: linear-gradient(62deg, #171a1b 0%, #342798 53%, #7e008d);
	$calendarBordersColor: rgba($black, 0.5);
	$buttonBackgroundColor: $calendarBordersColor;
	$buttonBackgroundHoverColor: $textColor;

	.pixellot-date-range-picker {
		width: 20px;
		height: 24px;

		.mx-input-wrapper:hover {
			.mx-icon-clear {
				display: none;
			}
			.mx-icon-calendar {
				display: block;
			}
		}

		.mx-icon-calendar, .mx-icon-clear {
			padding-top: 5px;
			font-size: 20px;
		}
		.mx-icon-clear {
			display: none;
			color: $white;
			transition: 0.1s color;
			&:hover {
				color: rgba($white, 0.8);
			}
		}
		.mx-input-wrapper {
			position: static;
			cursor: pointer;
		}
	}

	.pixellot-date-range-picker-popup {
		color: $white;
		background-image: $calendarBackgroundImage;
		border-color: $calendarBordersColor;
		border-radius: 8px;
		top: 24px !important;

		.mx-calendar-content.cell.active {
			background-color: $buttonBackgroundColor;
		}
		.mx-datepicker-btn-confirm {
			width: 70px;
			background-color: $buttonBackgroundColor;
			color: rgba($white, 0.7);
			font-size: 14px;
			border-radius: 60px;
			height: 32px;
			outline: none;
			border: none;
			transition: 0.3s color, 0.3s background-color;
			&:hover {
				color: $white;
				background-color: $buttonBackgroundHoverColor;
			}
		}

		.mx-datepicker-footer, .mx-datepicker-sidebar + .mx-datepicker-content, .mx-calendar + .mx-calendar {
			border-color: $calendarBordersColor;
		}

		.mx-btn-text {
			color: $white;
			transition: 0.3s color;
		}

		.mx-table-date .cell.not-current-month {
			color: rgba(#ccc, 0.7);
		}

		.mx-table-date .today {
			color: #84c9dd;
		}
	}
</style>
